import type { PlanCategory } from '@/TokamapEditor/helpers/planLayerMapping';
import type { PlanDoubt }    from '@models/map/dxfTypes';
import type { PlanShape }    from '@models/map/dxfTypes';

/**
 * **La file des doutes** : ce que l'opérateur doit arbitrer, et rien d'autre
 * (`docs/PLAN_PARCOURS_IMPORT.md` §4).
 *
 * ## Le problème
 *
 * Relire 146 formes est impossible ; en arbitrer six prend une minute. Le serveur sait déjà
 * DÉSIGNER les six — c'est tout l'objet de `PlanDoubtDetector`. Ce module tient l'état de ce
 * que l'opérateur en a fait, et le traduit en réglages d'import.
 *
 * ## Rien n'est écrit avant l'étape 3
 *
 * Un doute tranché ne quitte pas la file, il change de couleur. C'est délibéré : l'opérateur
 * doit pouvoir revenir sur un arbitrage, et un doute qui disparaît au clic fait perdre le
 * compte (« combien en ai-je vu ? »). Les arbitrages ne partent au serveur qu'avec le mapping,
 * sous la forme de `shapes` / `forcedShapes`.
 *
 * ## L'identité d'un doute, c'est celle de sa forme
 *
 * `layer` + `index`, comme partout ailleurs dans ce parcours. On n'invente pas un identifiant
 * de doute : deux analyses successives du même plan doivent retrouver les mêmes arbitrages,
 * et un numéro d'ordre de doute changerait à la première correction.
 */

/** Ce que l'opérateur a décidé d'une forme signalée. */
export type PlanArbitration = 'kept' | 'discarded';

/**
 * Arbitrages en cours, indexés par {@link doubtKey}.
 *
 * Une forme ABSENTE de cette table n'est pas « non décidée par défaut » : elle suit le sort
 * ordinaire de son motif — une forme non reconnue tombe, une forme sans numéro s'importe
 * muette. C'est ce qui fait que l'étape se traverse sans un clic sur un plan sain.
 */
export type PlanArbitrations = Record<string, PlanArbitration>;

/** Correction de numéro saisie sur une carte de doute, indexée par {@link doubtKey}. */
export type PlanCorrections = Record<string, string>;

/**
 * Séparateur de {@link doubtKey}.
 *
 * Un caractère qu'un nom de calque ne peut PAS contenir. Ni un espace ni un tiret ne
 * conviennent : un pseudo-calque s'appelle `fill rgb(3%, 36%, 58%) · stroke rgb(11%, …) · 0.22`
 * et porte déjà espaces, virgules, points et parenthèses. Une collision de clé ferait porter
 * l'arbitrage d'une forme sur une autre — silencieusement, la plus mauvaise des issues.
 */
const KEY_SEPARATOR = '\u0000';

/** Clé stable d'un doute : le couple qui désigne sa forme, et rien d'autre. */
export function doubtKey(doubt: Pick<PlanDoubt, 'layer' | 'index'>): string {
	return `${doubt.layer}${KEY_SEPARATOR}${doubt.index}`;
}

/**
 * Décision par DÉFAUT d'un motif, celle qui s'applique si l'opérateur ne touche à rien.
 *
 * C'est le comportement actuel de l'import, rendu explicite : une forme non reconnue tombe,
 * tout le reste passe. Le dire ici plutôt que de le laisser implicite permet à la carte
 * d'afficher « sera écartée » AVANT que quiconque ait cliqué — l'opérateur voit la
 * conséquence, il ne la découvre pas.
 */
export function defaultArbitration(doubt: PlanDoubt): PlanArbitration {
	return 'not_simple' === doubt.kind ? 'discarded' : 'kept';
}

/** Décision effective : celle de l'opérateur, à défaut celle du motif. */
export function arbitrationOf(doubt: PlanDoubt, arbitrations: PlanArbitrations): PlanArbitration {
	return arbitrations[doubtKey(doubt)] ?? defaultArbitration(doubt);
}

/** L'opérateur a-t-il tranché CE doute lui-même ? Ce qui reste est ce qu'il n'a pas vu. */
export function isSettled(doubt: PlanDoubt, arbitrations: PlanArbitrations): boolean {
	return undefined !== arbitrations[doubtKey(doubt)];
}

/** Nombre de doutes que l'opérateur n'a pas encore regardés. */
export function remainingCount(doubts: PlanDoubt[], arbitrations: PlanArbitrations): number {
	return doubts.filter(doubt => !isSettled(doubt, arbitrations)).length;
}

/**
 * Index du prochain doute à montrer, en repartant de `from` et en bouclant.
 *
 * On saute ce qui est déjà tranché : la file se parcourt une fois, pas en rond. `null` quand
 * tout est arbitré — l'écran affiche alors « rien à vérifier » plutôt qu'une carte vide.
 */
export function nextUnsettled(
	doubts: PlanDoubt[],
	arbitrations: PlanArbitrations,
	from: number,
): number | null {
	for (let step = 1; step <= doubts.length; step++) {
		const index = (from + step) % doubts.length;
		const doubt = doubts[index];
		if (doubt && !isSettled(doubt, arbitrations)) return index;
	}

	return null;
}

/**
 * Applique les arbitrages aux catégories du mapping — **le seul endroit où une décision de
 * l'opérateur devient un réglage d'import**.
 *
 * Les arbitrages portent sur des FORMES, pas sur des doutes : un clic sur la carte des formes
 * en produit autant qu'une carte de la file, et les deux gestes disent la même chose. C'est
 * pourquoi cette fonction lit `arbitrations` directement plutôt que de parcourir les doutes —
 * une forme que rien ne signalait doit pouvoir être écartée d'un clic.
 *
 * Deux gestes, deux champs, et ils sont symétriques :
 *
 *   - **écarter** une forme retenue → on la retire des `shapes` de sa catégorie, qui passe
 *     donc d'un fourre-tout de calque (`shapes: null`) à une liste explicite. C'est le
 *     mécanisme d'exclusion qui existait déjà ;
 *   - **garder** une forme rejetée → on l'ajoute aux `forcedShapes`, l'exception nominative à
 *     la liste blanche de formes simples. L'import la crée alors en polygone LIBRE.
 *
 * ⚠️ **Écarter une forme fige la catégorie sur les rangs actuellement connus.** Une catégorie
 * sans `shapes` prend toutes les formes de son calque, y compris celles qu'un plan révisé
 * ajouterait ; en lui donnant une liste, on la referme. C'est le prix de l'exclusion forme par
 * forme, et c'est pour ça qu'on ne l'écrit QUE lorsqu'une forme est réellement écartée —
 * `shapes` reste `null` tant que personne n'exclut rien.
 */
export function applyArbitrations(params: {
	categories: PlanCategory[];
	/** Toutes les formes du plan : c'est `shape` qui dit si un « Garder » doit forcer ou non. */
	shapes: PlanShape[];
	arbitrations: PlanArbitrations;
	/** Numéros saisis, indexés par {@link doubtKey}. */
	corrections: PlanCorrections;
}): PlanCategory[] {
	const { categories, shapes, arbitrations, corrections } = params;

	const discarded = new Map<string, Set<number>>();
	const forced = new Map<string, Set<number>>();
	const numbers = new Map<string, Record<number, string>>();
	const byKey = new Map(shapes.map(shape => [doubtKey(shape), shape]));
	const population = ranksByLayer(shapes);

	// Un numéro saisi part avec le mapping et ÉCRASE celui que l'appariement a trouvé. Il vaut
	// quelle que soit la décision : corriger un numéro puis écarter la forme est contradictoire,
	// mais c'est le geste d'écart qui tranche — le numéro suivra si l'opérateur revient dessus.
	for (const [key, value] of Object.entries(corrections)) {
		const shape = byKey.get(key);
		const typed = value.trim();
		if (!shape || !typed) continue;

		const ofLayer = numbers.get(shape.layer) ?? {};
		ofLayer[shape.index] = typed;
		numbers.set(shape.layer, ofLayer);
	}

	for (const [key, decision] of Object.entries(arbitrations)) {
		const shape = byKey.get(key);
		if (!shape) continue;

		// `shape === null` = contour hors liste blanche. Le garder demande une exception
		// nominative ; le garder alors qu'il passait déjà n'a rien à écrire.
		const importable = null !== shape.shape && undefined !== shape.shape;
		const target = 'discarded' === decision ? discarded : (importable ? null : forced);
		if (!target) continue;

		const set = target.get(shape.layer) ?? new Set<number>();
		set.add(shape.index);
		target.set(shape.layer, set);
	}

	if (!discarded.size && !forced.size && !numbers.size) return categories;

	const ranksByLayerMap = population;

	return categories.map(category => {
		const excluded = discarded.get(category.baseLayer);
		const kept = forced.get(category.baseLayer);
		const typed = numbers.get(category.baseLayer);
		if (!excluded && !kept && !typed) return category;

		const patch: Partial<PlanCategory> = {};

		if (typed) patch.numbers = { ...category.numbers, ...typed };

		if (excluded?.size) {
			const ranks = category.shapes ?? ranksByLayerMap.get(category.baseLayer) ?? [];
			const remaining = ranks.filter(rank => !excluded.has(rank));

			// ⚠️ Une liste VIDE ne veut pas dire « aucune forme » côté serveur : `parseShapes()`
			// la lit comme `null`, c'est-à-dire « TOUTES les formes du calque » — exactement
			// l'inverse de ce qu'on demande. Une catégorie vidée se DÉCOCHE donc, ce qui la sort
			// du mapping envoyé ; c'est aussi ce que l'opérateur attend de voir dans la liste
			// après avoir écarté sa dernière forme.
			if (remaining.length) patch.shapes = remaining;
			else patch.enabled = false;
		}

		if (kept?.size) {
			// Les rangs forcés s'AJOUTENT à ceux déjà forcés : deux passages sur la file ne
			// doivent pas défaire le premier.
			patch.forcedShapes = [...new Set([...(category.forcedShapes ?? []), ...kept])].sort((a, b) => a - b);
			// Garder une forme non reconnue RECOCHE sa ligne : elle en était sortie parce
			// qu'aucune de ses formes n'était importable, et c'est précisément ce que le repli
			// en polygone libre vient de changer. Sans ça, « Garder » n'enverrait rien.
			patch.enabled = true;
		}

		return { ...category, ...patch };
	});
}

/**
 * Rangs présents dans le plan, par calque — la population de référence d'une exclusion.
 *
 * Tirée des formes RÉELLEMENT reçues, et non recalculée : c'est la seule liste dont on soit
 * sûr qu'elle corresponde à ce que le serveur a énuméré. Un plafonnement de l'aperçu
 * (`truncated`) la rend incomplète, et c'est précisément pourquoi l'écran annonce le
 * plafonnement au lieu de le taire.
 */
export function ranksByLayer(shapes: { layer: string; index: number }[]): Map<string, number[]> {
	const byLayer = new Map<string, number[]>();

	for (const shape of shapes) {
		const ranks = byLayer.get(shape.layer) ?? [];
		ranks.push(shape.index);
		byLayer.set(shape.layer, ranks);
	}

	return byLayer;
}
